import { useClient } from '../supabase/client'

import { useQuery } from '@tanstack/vue-query'
import type { Database } from '@xleddyl/types'

export type DashboardViewName = keyof Database['dashboard']['Views']
export type DashboardViewRow<T extends DashboardViewName> = Database['dashboard']['Views'][T]['Row']

export type ResourceQueryData<
   TViewName extends DashboardViewName,
   S extends boolean,
> = S extends true ? DashboardViewRow<TViewName> | null : DashboardViewRow<TViewName>[] | null

export interface ResourceQueryConfig<
   TViewName extends DashboardViewName,
   P extends { enabled?: boolean },
   S extends boolean = boolean,
> {
   queryKey: string
   view: TViewName
   select?: string
   order?: { column: string; ascending?: boolean }[]
   maybeSingle?: S
   filters?: (query: any, props: P) => void
}

export const createResourceQuery = <
   TViewName extends DashboardViewName,
   P extends { enabled?: boolean },
   S extends boolean = boolean,
>(
   config: ResourceQueryConfig<TViewName, P, S>
) => {
   return (props: MaybeRef<P>) => {
      const client = useClient()

      const query = useQuery({
         enabled: computed(() => toValue(props).enabled),
         queryKey: [config.queryKey, props] as any,
         queryFn: async (): Promise<ResourceQueryData<TViewName, S>> => {
            const p = toValue(props)

            const query = client
               .schema('dashboard')
               .from(config.view as any)
               .select(config.select ?? '*')

            config.filters?.(query, p)

            for (const order of config.order ?? []) {
               query.order(order.column, { ascending: order.ascending ?? true })
            }

            if (config.maybeSingle) {
               const { data } = await query.maybeSingle().throwOnError()
               return data as unknown as ResourceQueryData<TViewName, S>
            }

            const { data } = await query.throwOnError()
            return data as unknown as ResourceQueryData<TViewName, S>
         },
      })

      onServerPrefetch(async () => {
         if (toValue(props).enabled !== false) {
            await query.suspense()
         }
      })

      return query
   }
}
